import type { CloseOverviewEstimatedTimeByMember } from "@common/schemas/clientsCloseOverview.schema";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import type { TooltipSections } from "@design-system/Tooltip/Tooltip.types";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";

/** The work row fields the estimate cell and its breakdown read. */
export type WorkEstimateRow = Pick<
    ClientsWorkOverviewWorkRow,
    "estimatedTimeRemaining" | "estimatedTimeDone" | "estimatedTimeByMember"
>;

export const formatEstimateMinutes = (minutes: number) =>
    formatDuration(minutes) || "0m";

const memberLabel = (member: CloseOverviewEstimatedTimeByMember) => {
    if (member.userId == null) {
        return "Unassigned";
    }
    return member.preferredName ?? member.email ?? "Unknown";
};

/**
 * The estimate breakdown on every surface: Remaining and Done, then each
 * member's remaining / done. It shows raw remaining and done whatever the
 * cell's variant: the first section is the legend for the member rows.
 */
export const buildWorkEstimateTooltip = ({
    row: { estimatedTimeRemaining, estimatedTimeDone, estimatedTimeByMember },
    title,
}: {
    row: WorkEstimateRow;
    /** Heads the breakdown, e.g. the field's name where nothing else says it. */
    title?: string;
}): { title?: string; sections: TooltipSections } => ({
    title,
    sections: [
        [
            {
                label: "Remaining",
                value: formatEstimateMinutes(estimatedTimeRemaining),
            },
            { label: "Done", value: formatEstimateMinutes(estimatedTimeDone) },
        ],
        estimatedTimeByMember.map((member) => ({
            label: memberLabel(member),
            value: formatEstimateMinutes(member.remainingMinutes),
            detail: formatEstimateMinutes(member.doneMinutes),
        })),
    ],
});
